/** German texts of the administration (company and platform). English in admin.en.ts. */
export const adminDe = {
  title: 'Verwaltung',
  description: 'Konten, Rechte und Einstellungen von {{company}}.',
  areas: { company: 'Firma', platform: 'Plattform' },
  platformHint: 'Gilt für alle Firmen der Gruppe.',
  sections: {
    accounts: { label: 'Konten', description: 'Personen der Firma, Sperren und Rechte.' },
    groups: { label: 'Gruppen', description: 'Personengruppen, eigene und aus dem SSO.' },
    roles: { label: 'Rollen', description: 'Bündel aus Berechtigungen und wer sie hat.' },
    settings: { label: 'Einstellungen', description: 'Vorgaben für alle Personen der Firma.' },
    sso: { label: 'Anmeldung', description: 'SSO-Anbieter der Firma und Anmeldung mit Passwort.' },
    notifications: {
      label: 'Benachrichtigungen',
      description: 'E-Mail, Teams und Push-Nachrichten als Kanäle der Firma.',
    },
    integrations: {
      label: 'Integrationen',
      description: 'SharePoint als Ablage für Dokumente der Module.',
    },
    customFields: { label: 'Zusatzfelder', description: 'Eigene Felder für Einträge der Module.' },
    shares: { label: 'Freigaben', description: 'Einträge, die andere Firmen lesen dürfen.' },
    privacy: { label: 'Datenschutz', description: 'Löschfristen, Auskunft und Löschung.' },
    ai: { label: 'KI', description: 'Anbieter, Datenstufe und KI-Funktionen der Firma.' },
    branding: { label: 'Erscheinungsbild', description: 'Logo und Firmenfarbe.' },
    audit: { label: 'Audit-Log', description: 'Wer hat wann was geändert.' },
    companies: { label: 'Firmen', description: 'Firmen und Teilgruppen der Gruppe.' },
    modules: { label: 'Module', description: 'Standard für alle Firmen und Schalter pro Firma.' },
    companyModules: {
      label: 'Module',
      description: 'Welche Module die Firma nutzt und in welcher Reihenfolge.',
    },
    platformAccess: {
      label: 'Plattformrechte',
      description: 'Superadmins, Plattformrechte und verknüpfte Konten.',
    },
    platformAudit: {
      label: 'Plattform-Audit',
      description: 'Änderungen an Firmen, Modulen und Plattformrechten.',
    },
    operations: {
      label: 'Betrieb',
      description: 'Zustand von Server, Datenbank, Jobs und Diensten.',
    },
  },
  errors: {
    'version-conflict':
      'Jemand anderes hat das inzwischen geändert. Die Seite zeigt jetzt den aktuellen Stand, bitte erneut versuchen.',
    'rate-limited': 'Zu viele Anfragen. Bitte kurz warten und erneut versuchen.',
    forbidden: 'Dafür fehlt die Berechtigung.',
    'invalid-input': 'Bitte die Eingaben prüfen.',
    notFound: 'Nicht mehr vorhanden. Vielleicht hat es jemand gelöscht.',
  },
  accounts: {
    title: 'Konten',
    description:
      'Konten entstehen bei der ersten Anmeldung per SSO oder werden hier für die Anmeldung mit Passwort angelegt.',
    search: 'Name oder E-Mail',
    statusFilter: 'Status',
    allStatuses: 'Alle',
    columns: {
      name: 'Name',
      email: 'E-Mail',
      login: 'Anmeldung',
      status: 'Status',
      lastLogin: 'Letzte Anmeldung',
    },
    status: { active: 'Aktiv', locked: 'Gesperrt' },
    lockReason: { admin: 'von der Verwaltung gesperrt', provider: 'vom SSO-Anbieter gesperrt' },
    never: 'noch nie',
    empty: 'Keine Konten gefunden',
    emptyHint: 'Suche ändern oder Filter entfernen.',
    loadMore: 'Weitere laden',
    create: 'Konto anlegen',
    createHint:
      'Für Personen ohne SSO. Sie bekommen eine Einladung per E-Mail und legen damit ihr Passwort fest.',
    name: 'Name',
    loginEmail: 'E-Mail-Adresse',
    loginEmailHint: 'Damit meldet sich die Person an. Die Einladung geht an diese Adresse.',
    emailInvalid: 'Bitte eine vollständige E-Mail-Adresse eingeben.',
    invite: 'Anlegen und einladen',
    created: 'Konto angelegt, die Einladung ist unterwegs.',
    createdWithoutMail:
      'Konto angelegt, aber die Einladung ging nicht raus. Auf der Seite des Kontos erneut senden.',
    login: {
      sso: 'SSO',
      password: 'Passwort',
      invited: 'Eingeladen',
      twoFactor: 'Authenticator',
      passkey: 'Passkey',
      none: 'Keine',
    },
    errors: {
      'account-locked': 'Das Konto ist gesperrt. Zuerst entsperren.',
      'cannot-reset-self':
        'Die eigene Authenticator-App wird im Profil entfernt, nicht in der Verwaltung.',
      'login-email-taken': 'Mit dieser E-Mail-Adresse meldet sich in der Firma schon ein Konto an.',
      'mail-not-sent': 'Die E-Mail ging nicht raus. Bitte später erneut senden.',
      'no-email': 'Für das Konto ist keine E-Mail-Adresse bekannt.',
      'password-login-disabled':
        'Die Firma meldet sich nur über SSO an. Die Anmeldung mit Passwort lässt sich unter Anmeldung erlauben.',
      'platform-account':
        'Das Konto hat Rechte auf der ganzen Plattform. Links und das Zurücksetzen der App kommen nur von einem Superadmin.',
    },
    detail: {
      account: 'Konto',
      createdAt: 'Angelegt',
      ssoManaged: 'Name und E-Mail kommen aus dem SSO der Firma und werden dort geändert.',
      appManaged: 'Konto mit Anmeldung per Passwort, angelegt in der STAW App.',
      login: 'Anmeldung',
      loginHint: 'Links zum Passwort gehen per E-Mail an die Person, nie an die Verwaltung.',
      ssoYes: 'Über den SSO-Anbieter der Firma',
      ssoNo: 'Nein',
      loginEmail: 'E-Mail-Adresse für die Anmeldung',
      passwordState: { set: 'Festgelegt', invited: 'Einladung offen', none: 'Keins' },
      twoFactor: 'Authenticator-App',
      twoFactorOn: 'Eingerichtet',
      twoFactorOff: 'Nicht eingerichtet',
      passkeys: 'Passkeys',
      passkeyCount_one: '{{count}} Passkey',
      passkeyCount_other: '{{count}} Passkeys',
      passkeysNone: 'Keine',
      sendInvite: 'Einladung senden',
      sendReset: 'Link zum Zurücksetzen senden',
      linkSent: 'Link per E-Mail an {{email}} geschickt',
      addPasswordTitle: 'Zusätzlich mit Passwort anmelden?',
      addPasswordHint:
        'Das Konto kommt aus dem SSO. Mit dem Link an {{email}} meldet es sich auch mit E-Mail-Adresse und Passwort an, solange die Firma das neben SSO erlaubt.',
      resetTwoFactor: 'Zweiten Faktor zurücksetzen',
      resetTwoFactorTitle: 'Authenticator-App und Passkeys von {{name}} entfernen?',
      resetTwoFactorHint:
        'Für eine Person, die ihr Handy oder ihren Passwort-Manager verloren hat, oder wenn jemand anderes einen Passkey hinzugefügt hat. Ihre Sitzungen enden, sie meldet sich danach nur mit Passwort an und richtet App und Passkeys im Profil neu ein. Sie bekommt eine E-Mail darüber.',
      twoFactorRemoved: 'Authenticator-App und Passkeys entfernt',
      groups: 'Gruppen',
      noGroups: 'In keiner Gruppe.',
      addGroup: 'Zu Gruppe hinzufügen',
      groupAdded: 'Zu {{group}} hinzugefügt',
      removeGroup: 'Aus {{group}} entfernen',
      groupRemoved: 'Aus {{group}} entfernt',
      addRole: 'Rolle zuweisen',
      chooseRole: 'Rolle wählen',
      searchRole: 'Rolle suchen',
      removeRole: 'Rolle {{role}} entfernen',
      roles: 'Rollen',
      noRoles: 'Keine Rolle zugewiesen. Die Person sieht nur, was ohne Rechte sichtbar ist.',
      direct: 'Direkt zugewiesen',
      viaGroup: 'Über Gruppe {{group}}',
      viaEveryone: 'Für alle Konten der Firma',
      until: 'bis {{date}}',
      permissions: 'Berechtigungen',
      permissionsHint: 'Was die Person darf und aus welcher Rolle es stammt.',
      noPermissions: 'Keine Berechtigungen.',
      from: 'aus {{roles}}',
      lock: 'Konto sperren',
      unlock: 'Entsperren',
      lockTitle: 'Konto von {{name}} sperren?',
      lockDescription:
        'Alle Sitzungen enden sofort. Anmelden geht erst wieder nach dem Entsperren.',
      locked: 'Konto gesperrt',
      unlocked: 'Konto entsperrt',
      cannotLockSelf: 'Das eigene Konto lässt sich nicht sperren.',
    },
    privacy: {
      title: 'Datenschutz',
      description: 'Anfragen der Person nach Art. 15, 20 und 17 DSGVO.',
      exportHint:
        'Alle Daten, die die STAW App zu dieser Person speichert, aus Kern und Modulen, als JSON-Datei.',
      export: 'Daten exportieren',
      exported: 'Export heruntergeladen. Der Abruf steht im Audit-Log.',
      fileName: 'personenbezogene-daten-{{date}}.json',
      eraseHint:
        'Löscht oder anonymisiert alle Daten der Person. Das Konto bleibt ohne Namen und E-Mail, damit Verlauf und Audit-Log stimmig bleiben.',
      eraseLocked: 'Nur gesperrte Konten lassen sich löschen. Zuerst das Konto sperren.',
      eraseSelf: 'Das eigene Konto lässt sich nicht löschen.',
      erase: 'Person löschen',
      eraseTitle: 'Alle Daten von {{name}} löschen?',
      eraseDescription:
        'Kern und Module löschen oder anonymisieren alle Daten zu dieser Person. Das lässt sich nicht rückgängig machen. Daten mit gesetzlicher Aufbewahrungsfrist bleiben bis zu deren Ende.',
      erased: 'Person gelöscht',
    },
  },
  groups: {
    title: 'Gruppen',
    description: 'Rollen an eine Gruppe vergeben, statt an jede Person einzeln.',
    new: 'Neue Gruppe',
    columns: { name: 'Name', members: 'Mitglieder', source: 'Herkunft' },
    source: { local: 'Eigene', provider: 'SSO' },
    empty: 'Noch keine Gruppen',
    emptyHint: 'Gruppen bündeln Personen, z. B. alle Projektleitungen.',
    name: 'Name',
    descriptionLabel: 'Beschreibung',
    create: 'Gruppe anlegen',
    created: 'Gruppe angelegt',
    edit: 'Bearbeiten',
    editTitle: 'Gruppe bearbeiten',
    saved: 'Gespeichert',
    delete: 'Gruppe löschen',
    deleteTitle: 'Gruppe {{name}} löschen?',
    deleteDescription: 'Mitglieder verlieren die Rollen, die über diese Gruppe zugewiesen sind.',
    deleted: 'Gruppe gelöscht',
    members: 'Mitglieder',
    memberCount_one: '{{count}} Mitglied',
    memberCount_other: '{{count}} Mitglieder',
    noMembers: 'Noch keine Mitglieder.',
    addMember: 'Person hinzufügen',
    choosePerson: 'Person wählen',
    searchPerson: 'Name oder E-Mail',
    memberAdded: '{{name}} hinzugefügt',
    removeMember: '{{name}} aus der Gruppe entfernen',
    memberRemoved: '{{name}} entfernt',
    providerManaged: 'Diese Gruppe kommt aus dem SSO. Name und Mitglieder werden dort gepflegt.',
  },
  roles: {
    title: 'Rollen',
    description: 'Eine Rolle bündelt Berechtigungen. Vergeben wird sie an Personen oder Gruppen.',
    new: 'Neue Rolle',
    report: 'Rezertifizierung',
    columns: { name: 'Name', permissions: 'Berechtigungen', assignments: 'Zuweisungen' },
    template: 'Vorlage',
    empty: 'Noch keine Rollen',
    name: 'Name',
    descriptionLabel: 'Beschreibung',
    templateHint: 'Vorlage der STAW App. Name und Berechtigungen lassen sich anpassen.',
    permissions: 'Berechtigungen',
    permissionsHint:
      'Was die Rolle erlaubt. Bei manchen Berechtigungen lässt sich zusätzlich der Umfang wählen.',
    searchPermissions: 'Berechtigung suchen',
    selected_one: '{{count}} ausgewählt',
    selected_other: '{{count}} ausgewählt',
    allOf: 'Alle Berechtigungen dieses Bereichs, auch künftige',
    scope: 'Umfang',
    scopes: { own: 'Eigene', team: 'Team', 'org-unit': 'Org-Einheit', all: 'Alle' },
    privileged: 'Verwaltungsrecht',
    privilegedHint: 'Nur mit MFA-Anmeldung wirksam.',
    sensitive: 'Sensibel',
    sensitiveHint: 'Jeder Zugriff wird protokolliert.',
    noPermissions: 'Keine Berechtigungen gefunden.',
    create: 'Rolle anlegen',
    created: 'Rolle angelegt',
    save: 'Änderungen speichern',
    saved: 'Gespeichert',
    unsaved: 'Nicht gespeicherte Änderungen',
    discard: 'Verwerfen',
    delete: 'Rolle löschen',
    deleteTitle: 'Rolle {{name}} löschen?',
    deleteDescription: 'Alle Zuweisungen dieser Rolle enden sofort.',
    deleted: 'Rolle gelöscht',
    assignments: 'Zuweisungen',
    assignmentsHint: 'Wer diese Rolle hat. Befristete Zuweisungen enden von selbst.',
    noAssignments: 'Noch niemandem zugewiesen.',
    assign: 'Zuweisen',
    assignTitle: 'Rolle {{name}} zuweisen',
    assignTo: 'Zuweisen an',
    person: 'Person',
    group: 'Gruppe',
    everyone: 'Alle Konten',
    everyoneHint: 'Jedes Konto der Firma, auch Konten, die später dazukommen.',
    chooseGroup: 'Gruppe wählen',
    searchGroup: 'Gruppe suchen',
    validUntil: 'Gültig bis',
    validUntilHint: 'Leer lassen für unbefristet, z. B. bei Urlaubsvertretungen ausfüllen.',
    unlimited: 'unbefristet',
    assigned: 'Zugewiesen',
    unassign: 'Zuweisung an {{name}} entfernen',
    unassigned: 'Zuweisung entfernt',
    invalidPermission: 'Eine Berechtigung passt nicht mehr. Bitte die Auswahl prüfen.',
    notForEveryone:
      'Die Rolle gilt für alle Konten. Verwaltungsrechte und sensible Berechtigungen sind dafür nicht erlaubt.',
    owners: { company: 'Verwaltung der Firma', core: 'Allgemein' },
    reportTitle: 'Rezertifizierung',
    reportDescription:
      'Alle aktiven Zuweisungen. Grundlage für die regelmäßige Prüfung, wer welche Rechte hat (ISO 27001).',
    reportColumns: {
      assignee: 'Zugewiesen an',
      role: 'Rolle',
      privileged: 'Verwaltungsrechte',
      since: 'Seit',
      until: 'Bis',
    },
    onlyPrivileged: 'Nur mit Verwaltungsrechten',
    selfAssigned: 'Selbst zugewiesen',
    noReport: 'Keine aktiven Zuweisungen.',
  },
  settings: {
    title: 'Einstellungen',
    description:
      'Vorgaben für alle Personen der Firma. Persönliche Einstellungen gelten, wo jemand selbst etwas gewählt hat.',
    companyDefault: 'Firmenvorgabe',
    removeDefault: 'Vorgabe entfernen',
    saved: 'Gespeichert',
    empty: 'Keine Einstellungen zum Ändern freigegeben.',
    sections: {
      company: 'Firma',
      personalDefaults: 'Vorgaben für persönliche Einstellungen',
      platform: 'Alle Firmen',
    },
    modules: {
      title: 'Einstellungen der Module',
      description:
        'Vorgaben, Kataloge und Vorlagen eines Moduls stehen auf seiner Einstellungsseite.',
    },
  },
  notifications: {
    title: 'Benachrichtigungen',
    description:
      'Kanäle, über die Personen der Firma auf neue Benachrichtigungen hingewiesen werden. Jede Person wählt im Profil selbst.',
    neutralHint:
      'Nachrichten außerhalb der App nennen nur die Zahl ungelesener Benachrichtigungen und verlinken die App, nie deren Inhalt.',
    saved: 'Gespeichert',
    save: 'Speichern',
    test: 'Test an mich senden',
    testSent: 'Test gesendet',
    secretStored: 'Gespeichert. Leer lassen, um es zu behalten.',
    secretMissing: 'Noch nicht hinterlegt.',
    hostInvalid: 'Bitte einen Servernamen angeben, z. B. smtp.firma.de.',
    portInvalid: 'Port von 1 bis 65535.',
    emailInvalid: 'Bitte eine gültige E-Mail-Adresse angeben.',
    idInvalid: 'Bitte eine ID im Format 00000000-0000-0000-0000-000000000000 angeben.',
    email: {
      title: 'E-Mail',
      description: 'Der Test geht an die eigene Adresse und nutzt die gespeicherte Einstellung.',
      modes: {
        platform: 'Server der STAW App',
        platformHint: 'Versand über den gemeinsamen Mailserver der Gruppe.',
        platformMissing: 'Auf dieser Installation ist kein gemeinsamer Mailserver eingerichtet.',
        own: 'Eigener SMTP-Server',
        ownHint: 'Versand über den Mailserver der Firma, z. B. mit eigener Absenderadresse.',
        off: 'Kein E-Mail-Versand',
      },
      host: 'Server',
      port: 'Port',
      security: 'Verschlüsselung',
      username: 'Benutzername',
      password: 'Passwort',
      fromAddress: 'Absenderadresse',
      fromName: 'Absendername',
    },
    teams: {
      title: 'Microsoft Teams',
      description: 'Hinweise im Aktivitätsfeed von Teams, für Konten aus Microsoft Entra ID.',
      enabled: 'Teams-Hinweise anbieten',
      setupHint:
        'Nötig sind eine App-Registrierung in Entra ID mit der Anwendungsberechtigung TeamsActivity.Send und die Teams-App der STAW App, verteilt an alle Personen. Die Schritte stehen in der Anleitung zu dieser Seite.',
      tenantId: 'Mandanten-ID (Tenant)',
      clientId: 'Anwendungs-ID (Client)',
      clientSecret: 'Geheimer Clientschlüssel',
      appId: 'ID der Teams-App',
      appIdHint: 'Aus dem Manifest der Teams-App, nicht die Anwendungs-ID.',
    },
    push: {
      title: 'Push-Nachrichten',
      description:
        'Hinweise in der Mitteilungszentrale von iPhone, iPad, Android, Windows und macOS. Die Nachricht ist bis zum Gerät verschlüsselt.',
      enabled: 'Push-Nachrichten anbieten',
      enabledHint:
        'Personen schalten sie im Profil für jedes Gerät einzeln ein. Der Test geht an die eigenen Geräte.',
    },
    errors: {
      'not-configured': 'Dieser Kanal ist noch nicht eingerichtet oder gespeichert.',
      'no-address': 'Für das eigene Konto fehlt die Adresse dieses Kanals.',
      'auth-failed': 'Die Anmeldung beim Dienst ist fehlgeschlagen. Zugangsdaten prüfen.',
      'connection-failed': 'Der Server ist nicht erreichbar.',
      rejected: 'Der Dienst hat die Nachricht abgelehnt.',
      'teams-incomplete': 'Für Teams fehlen Angaben.',
      'smtp-missing': 'Für den eigenen Server fehlen Angaben.',
      'password-required':
        'Server, Port oder Benutzername wurden geändert. Bitte das Passwort erneut eingeben.',
      'secret-required':
        'Mandanten- oder Anwendungs-ID wurden geändert. Bitte den geheimen Clientschlüssel erneut eingeben.',
    },
  },
  integrations: {
    title: 'Integrationen',
    description: 'Dienste anderer Anbieter, die die Firma an die STAW App anbindet.',
    saved: 'Gespeichert',
    save: 'Speichern',
    test: 'Verbindung testen',
    testSucceeded: 'Die Verbindung zu {{site}} funktioniert.',
    secretStored: 'Gespeichert. Leer lassen, um ihn zu behalten.',
    secretMissing: 'Noch nicht hinterlegt.',
    idInvalid: 'Bitte eine ID im Format 00000000-0000-0000-0000-000000000000 angeben.',
    sharepoint: {
      title: 'SharePoint',
      description:
        'Module legen Dokumente in einer Bibliothek der Firma ab, für jeden Eintrag in einem eigenen Ordner. Ohne SharePoint bleiben sie in der Dateiablage der STAW App.',
      enabled: 'Dokumente in SharePoint ablegen',
      enabledHint: 'Beim Speichern prüft die STAW App vorher die Verbindung.',
      verified: 'Verbunden mit {{site}}, zuletzt geprüft {{time}}.',
      unverified: 'Die Verbindung wurde noch nicht geprüft.',
      setupHint:
        'Nötig ist eine App-Registrierung in Entra ID mit der Graph-Anwendungsberechtigung Sites.Selected und Schreibrecht auf diese Website. Die Schritte stehen in der Anleitung zu dieser Seite.',
      tenantId: 'Mandanten-ID (Tenant)',
      clientId: 'Anwendungs-ID (Client)',
      credential: 'Anmeldung der App',
      credentials: {
        certificate: 'Zertifikat',
        certificateHint:
          'Empfohlen. Entra ID kennt nur den öffentlichen Teil, der private Schlüssel liegt verschlüsselt in der STAW App.',
        secret: 'Geheimer Clientschlüssel',
        secretHint: 'Läuft nach höchstens zwei Jahren ab und muss dann neu eingetragen werden.',
      },
      clientSecret: 'Geheimer Clientschlüssel',
      privateKey: 'Privater Schlüssel des Zertifikats (PEM)',
      thumbprint: 'Fingerabdruck des Zertifikats',
      thumbprintHint: 'Wie in Entra ID unter „Zertifikate & Geheimnisse“ angezeigt, 40 Zeichen.',
      thumbprintInvalid: 'Bitte die 40 Zeichen des Fingerabdrucks angeben.',
      site: 'Website',
      siteHint: 'Adresse der Website, z. B. https://firma.sharepoint.com/sites/Vertrieb',
      siteInvalid: 'Bitte die Adresse einer SharePoint-Website angeben, die mit https:// beginnt.',
      library: 'Bibliothek',
      libraryHint: 'Name der Dokumentbibliothek, z. B. Dokumente.',
      rootFolder: 'Stammordner',
      rootFolderHint:
        'Ordner in der Bibliothek, unter dem die Module ihre Ordner anlegen. Leer für die oberste Ebene.',
      folderInvalid: 'Ordnernamen ohne " * : < > ? \\ |, Ebenen mit / getrennt.',
    },
    errors: {
      'auth-failed':
        'Die Anmeldung bei Microsoft ist fehlgeschlagen. Mandanten-ID, Anwendungs-ID und Schlüssel prüfen.',
      forbidden: 'Die App hat keinen Zugriff auf die Website. Freigabe für Sites.Selected prüfen.',
      'site-not-found': 'Diese Website gibt es nicht.',
      'library-not-found': 'Die Website hat keine Bibliothek mit diesem Namen.',
      'folder-invalid':
        'Der Stammordner ist ungültig, oder eine Datei mit seinem Namen liegt im Weg.',
      'connection-failed': 'Microsoft ist gerade nicht erreichbar. Später erneut versuchen.',
      incomplete: 'Für die Verbindung fehlen Angaben. Erst vollständig speichern, dann testen.',
      'site-invalid': 'Bitte die Adresse einer SharePoint-Website angeben.',
      'private-key-invalid':
        'Der private Schlüssel muss ein RSA-Schlüssel im PEM-Format mit mindestens 2048 Bit sein.',
      'thumbprint-invalid': 'Bitte die 40 Zeichen des Fingerabdrucks angeben.',
      'secret-required':
        'Mandanten- oder Anwendungs-ID wurden geändert. Bitte Schlüssel oder Zertifikat erneut eingeben.',
    },
  },
  sso: {
    title: 'Anmeldung',
    description:
      'Die Personen der Firma melden sich über den SSO-Anbieter der Firma an, meist Microsoft Entra ID, oder mit E-Mail-Adresse und Passwort.',
    status: { draft: 'Entwurf', active: 'Aktiv', disabled: 'Deaktiviert' },
    notConfigured: 'Noch kein Anbieter eingerichtet.',
    provider: 'Anbieter',
    issuer: 'Issuer-URL',
    issuerHint: 'Bei Entra ID: https://login.microsoftonline.com/<Tenant-ID>/v2.0',
    issuerInvalid: 'Eine Adresse, die mit https:// beginnt.',
    clientId: 'Client-ID',
    clientSecret: 'Client-Secret',
    secretStored: 'Ein Secret ist gespeichert. Leer lassen, um es zu behalten.',
    secretMissing: 'Ohne Secret nur für Anbieter, die das erlauben (Public Client mit PKCE).',
    scopes: 'Scopes',
    scopesHint: 'Mit Leerzeichen getrennt, beginnt mit openid.',
    redirectUri: 'Weiterleitungs-URI',
    redirectUriHint: 'In der App-Registrierung beim Anbieter eintragen.',
    copy: 'Kopieren',
    copied: 'Kopiert',
    save: 'Speichern',
    saved: 'Gespeichert. Mit einem Test-Login aktivieren.',
    test: 'Test-Login',
    testHint: 'Aktiv wird der Anbieter erst nach einem erfolgreichen Test-Login.',
    activeHint: 'Jede Änderung setzt den Anbieter auf Entwurf, bis ein Test-Login gelingt.',
    verifiedAt: 'Zuletzt erfolgreich getestet',
    passwordLogin: {
      title: 'Anmeldung mit Passwort',
      label: 'Neben SSO auch mit E-Mail-Adresse und Passwort',
      description:
        'Für Personen ohne Konto beim SSO-Anbieter, z. B. externe Kräfte. Ausgeschaltet melden sich alle über SSO an.',
      inactive:
        'Solange kein SSO-Anbieter aktiv ist, melden sich alle mit E-Mail-Adresse und Passwort an.',
      enabled: 'Anmeldung mit Passwort erlaubt',
      disabled: 'Anmeldung mit Passwort beendet',
      offTitle: 'Anmeldung mit Passwort beenden?',
      offDescription:
        'Alle Sitzungen mit Passwort enden sofort. Konten ohne SSO kommen erst wieder in die App, wenn die Anmeldung mit Passwort erneut erlaubt ist.',
      off: 'Beenden',
      errors: {
        'sso-not-active': 'Erst den SSO-Anbieter mit einem Test-Login aktivieren.',
        'sso-session-required':
          'Zum Abschalten mit SSO anmelden. So ist sicher, dass die Verwaltung danach noch hineinkommt.',
      },
    },
    disable: 'Deaktivieren',
    disableTitle: 'SSO-Anmeldung deaktivieren?',
    disableDescription:
      'Danach melden sich alle mit E-Mail-Adresse und Passwort an. Konten aus dem SSO brauchen dafür zuerst einen Link zum Passwort von ihrer Kontoseite. Laufende Sitzungen bleiben bestehen.',
    disabled: 'Deaktiviert',
    protocol: 'Verfahren',
    protocols: {
      oidc: {
        label: 'OpenID Connect',
        description: 'Standard, bei Entra ID über eine App-Registrierung.',
      },
      saml: {
        label: 'SAML 2.0',
        description:
          'Für Anbieter, die nur SAML können oder es vorschreiben. Bei Entra ID über eine Unternehmensanwendung.',
      },
    },
    saml: {
      entityId: 'Entity-ID',
      ssoUrl: 'Anmelde-URL',
      certificates: 'Signaturzertifikate',
      accounts: 'Konten',
      accountsShared: 'Dieselben wie bei OpenID Connect (Entra-Tenant über Microsoft bestätigt)',
      accountsOwn: 'Eigene Konten dieses SAML-Anbieters',
      fingerprint: 'SHA-256 {{value}}',
      validUntil: 'gültig bis {{date}}',
      certificateExpiring:
        'Ein Signaturzertifikat läuft in weniger als 30 Tagen ab. Sobald der Identitätsanbieter ein neues hat, die Metadaten neu eintragen.',
      metadata: 'Metadaten des Identitätsanbieters',
      metadataKeep: 'Leer lassen, um die gespeicherten Metadaten zu behalten.',
      metadataRequired: 'Metadaten als XML oder als URL angeben.',
      source: { xml: 'XML', url: 'URL' },
      sourceHint: {
        xml: 'Bei Entra ID: "Verbundmetadaten-XML" der Unternehmensanwendung, als Datei oder eingefügt. Die Anmeldung legt eigene Konten an, getrennt von OpenID Connect.',
        url: 'Bei Entra ID: "App-Verbundmetadaten-URL". Nur öffentlich erreichbare https-Adressen. Kommt die URL von Microsoft, gelten dieselben Konten wie bei OpenID Connect.',
      },
      xmlPlaceholder: '<EntityDescriptor …>',
      upload: 'XML-Datei laden',
      attributes: 'Attribute',
      attributesHint:
        'Welche Attribute der SAML-Antwort Kennung, Name und E-Mail enthalten. Leer lassen für die Standardwerte von Entra ID.',
      attribute: { subject: 'Kennung', displayName: 'Name', email: 'E-Mail' },
      subjectHint:
        'Ordnet die Anmeldung dem Konto zu und darf sich nie ändern. Nach dem Start nicht mehr anpassen, sonst entstehen neue Konten. "NameID" nimmt die NameID der Antwort.',
      serviceProvider: 'Beim Identitätsanbieter eintragen',
      serviceProviderHint:
        'Bei Entra ID unter "Grundlegende SAML-Konfiguration" als Bezeichner und Antwort-URL.',
      spEntityId: 'Bezeichner (Entity-ID)',
      acsUrl: 'Antwort-URL (Assertion Consumer Service)',
      download: 'Metadaten herunterladen',
      errors: {
        invalid:
          'Die Metadaten lassen sich nicht verwenden. Nötig sind ein SAML-2.0-Anbieter, eine https-Anmelde-URL und ein Signaturzertifikat.',
        unreachable: 'Die Metadaten-URL ist nicht erreichbar. Alternativ die XML-Datei laden.',
      },
    },
  },
  branding: {
    title: 'Erscheinungsbild',
    description: 'Logo und Farbe der Firma. Layout und Bausteine bleiben für alle gleich.',
    logo: 'Logo',
    logoHint: 'SVG, PNG oder WebP bis 512 KB. Quadratisch wirkt am besten.',
    noLogo: 'Kein Logo. Die App zeigt das Kürzel der Firma.',
    upload: 'Logo hochladen',
    replace: 'Ersetzen',
    remove: 'Entfernen',
    uploaded: 'Logo gespeichert',
    removed: 'Logo entfernt',
    invalidLogo: 'Diese Datei passt nicht. Erlaubt sind SVG ohne Skripte, PNG und WebP bis 512 KB.',
    color: 'Firmenfarbe',
    colorHint:
      'Für aktive Navigation, Markierungen und Fokus. Varianten für hell und dunkel werden abgeleitet und auf Kontrast geprüft.',
    pickColor: 'Farbe wählen',
    colorEmpty: 'Ohne Eingabe gilt die Standardfarbe der STAW App.',
    invalidColor: 'Farbe als #rrggbb angeben.',
    useDefault: 'Standardfarbe',
    preview: 'Vorschau',
    previewButton: 'Schaltfläche',
    previewLink: 'Markierung',
    previewBadge: 'Neu',
    saved: 'Gespeichert',
  },
  shares: {
    title: 'Freigaben',
    description:
      'Einträge, die diese Firma anderen Firmen freigibt, und umgekehrt. Freigegebene Einträge sind nur lesbar.',
    direction: 'Richtung',
    outgoing: 'Von uns freigegeben',
    incoming: 'Für uns freigegeben',
    untitled: 'Eintrag ohne Titel',
    columns: {
      record: 'Eintrag',
      to: 'Freigegeben für',
      from: 'Freigegeben von',
      parts: 'Zusätzlich freigegeben',
      expires: 'Bis',
    },
    emptyOutgoing: 'Kein Eintrag ist für eine andere Firma freigegeben',
    emptyIncoming: 'Keine andere Firma gibt Einträge für diese Firma frei',
    emptyHint: 'Module, die es erlauben, bieten die Freigabe auf der Seite eines Eintrags an.',
  },
  privacy: {
    title: 'Datenschutz',
    description:
      'Wie lange die Firma personenbezogene Daten aufbewahrt. Die Fristen der Module und des Kerns sind Vorschläge, bis die Firma sie hier einschaltet.',
    requestsTitle: 'Auskunft und Löschung',
    requestsHint:
      'Anfragen einer Person nach Art. 15, 17 und 20 DSGVO laufen über die Seite ihres Kontos unter Verwaltung › Konten.',
    columns: { data: 'Daten', period: 'Frist', status: 'Status', lastRun: 'Letzter Lauf' },
    mode: {
      active: 'Eingeschaltet',
      off: 'Aus',
      automatic: 'Automatisch',
      erasure: 'Mit der Person',
    },
    proposal: 'Vorschlag, ab {{start}}',
    after: {
      label: 'ab {{start}}',
      'account-locked': 'Sperre des Kontos',
      'session-end': 'Ende der Sitzung',
      event: 'Ereignis',
      'account-erased': 'Löschung der Person',
      read: 'Lesen',
      'consent-withdrawn': 'Widerruf der Einwilligung',
      'switched-off': 'Ausschalten durch die Person',
      request: 'Anfrage',
      'job-finished': 'Ende des Auftrags',
    },
    entities: {
      core: {
        account: 'Konten',
        login: 'Anmeldedaten (Passwort, Authenticator-App)',
        session: 'Sitzungen',
        audit: 'Audit-Log',
        setting: 'Persönliche Einstellungen',
        notification: 'Benachrichtigungen',
        'notification-contact': 'Mobilnummern (früher WhatsApp)',
        'push-subscription': 'Geräte für Push-Nachrichten',
        'notification-dispatch': 'Versandzeitpunkte',
        'calendar-invite': 'Versendete Kalendereinladungen',
        event: 'Ereignisse zwischen Modulen',
        'idempotency-key': 'Gespeicherte Antworten',
        'ai-usage': 'Nutzung von KI-Funktionen',
        'saved-view': 'Gespeicherte Ansichten',
        signature: 'Unterschriften',
        job: 'Hintergrundaufträge',
      },
    },
    units: {
      D_one: '{{count}} Tag',
      D_other: '{{count}} Tage',
      M_one: '{{count}} Monat',
      M_other: '{{count}} Monate',
      Y_one: '{{count}} Jahr',
      Y_other: '{{count}} Jahre',
    },
    unitNames: { D: 'Tage', M: 'Monate', Y: 'Jahre' },
    lastRun_one: '{{date}}, {{count}} Eintrag',
    lastRun_other: '{{date}}, {{count}} Einträge',
    edit: 'Frist für {{name}} ändern',
    dialogTitle: 'Löschfrist: {{name}}',
    dialogDescription: 'Vorschlag: {{period}}. Mit dem Datenschutzbeauftragten abstimmen.',
    enabled: 'Frist anwenden',
    enabledDelete: 'Jede Nacht werden Einträge gelöscht, deren Frist abgelaufen ist.',
    enabledAnonymize: 'Jede Nacht werden Einträge anonymisiert, deren Frist abgelaufen ist.',
    amount: 'Dauer',
    amountInvalid: 'Eine ganze Zahl von 1 bis 9999',
    unit: 'Einheit',
    minimum: 'Mindestens {{minimum}}',
    tooShort: 'Die Frist muss mindestens {{minimum}} betragen.',
    warningTitle: 'Nicht umkehrbar',
    warning: 'Was älter ist, wird in der nächsten Nacht endgültig gelöscht oder anonymisiert.',
    saved: 'Löschfrist gespeichert',
  },
  customFields: {
    title: 'Zusatzfelder',
    description:
      'Eigene Felder für Einträge der Module, etwa eine Kostenstelle am Fahrzeug. Gelten nur für diese Firma.',
    entity: 'Eintrag',
    noEntities: 'Kein aktives Modul bietet Zusatzfelder an',
    noEntitiesHint: 'Module legen fest, welche ihrer Einträge sich um Felder erweitern lassen.',
    empty: 'Noch keine Zusatzfelder',
    emptyHint: 'Felder erscheinen auf der Seite jedes Eintrags, in der Reihenfolge dieser Liste.',
    new: 'Feld anlegen',
    editTitle: 'Feld bearbeiten',
    created: 'Feld angelegt',
    saved: 'Feld gespeichert',
    removed: '„{{name}}“ gelöscht',
    columns: { label: 'Bezeichnung', type: 'Art', required: 'Pflicht' },
    requiredBadge: 'Pflicht',
    labelDe: 'Bezeichnung (Deutsch)',
    labelEn: 'Bezeichnung (Englisch)',
    type: 'Art',
    types: {
      text: 'Text, einzeilig',
      textarea: 'Text, mehrzeilig',
      number: 'Zahl',
      date: 'Datum',
      boolean: 'Ja/Nein',
      select: 'Auswahl',
      multiselect: 'Mehrfachauswahl',
    },
    key: 'Schlüssel',
    keyHint:
      'Für Schnittstellen und Exporte. Lässt sich später nicht ändern, ebenso wenig die Art.',
    keyInvalid: 'Kleinbuchstaben, Ziffern und Bindestriche, beginnend mit einem Buchstaben.',
    required: 'Pflichtfeld',
    requiredHint: 'Gilt beim nächsten Speichern eines Eintrags, nicht rückwirkend.',
    helpDe: 'Hilfetext (Deutsch)',
    helpEn: 'Hilfetext (Englisch)',
    bothLanguages: 'Bitte auf Deutsch und Englisch ausfüllen.',
    options: 'Auswahlmöglichkeiten',
    optionDe: 'Möglichkeit {{number}}, Deutsch',
    optionEn: 'Möglichkeit {{number}}, Englisch',
    optionPlaceholderDe: 'Deutsch',
    optionPlaceholderEn: 'Englisch',
    addOption: 'Möglichkeit hinzufügen',
    removeOption: 'Möglichkeit {{number}} entfernen',
    optionsRequired: 'Mindestens eine Möglichkeit angeben.',
    optionsHint:
      'Wird eine Möglichkeit entfernt, erscheint sie in Einträgen, die sie gewählt hatten, nicht mehr.',
    moveUp: '„{{name}}“ nach oben',
    moveDown: '„{{name}}“ nach unten',
    remove: '„{{name}}“ löschen',
    removeTitle: '„{{name}}“ löschen?',
    removeDescription:
      'Das Feld und seine Werte in allen Einträgen der Firma werden gelöscht. Das lässt sich nicht rückgängig machen.',
    removeConfirm: 'Feld löschen',
    exists: 'Ein Feld mit diesem Schlüssel gibt es schon.',
    limit: 'Mehr als 50 Felder pro Eintrag sind nicht möglich.',
  },
  audit: {
    title: 'Audit-Log',
    description:
      'Jede Änderung und jeder Zugriff auf geschützte Daten. Einträge sind unveränderlich.',
    platformTitle: 'Plattform-Audit',
    platformDescription:
      'Änderungen an Firmen, Modulen, Plattformrechten und globalen Einstellungen.',
    readHint: 'Auch das Lesen dieses Protokolls wird protokolliert.',
    columns: {
      time: 'Zeitpunkt',
      actor: 'Wer',
      action: 'Aktion',
      target: 'Objekt',
      outcome: 'Ergebnis',
    },
    outcome: { success: 'Erfolgreich', denied: 'Abgelehnt', failure: 'Fehlgeschlagen' },
    system: 'System',
    superadmin: 'Superadmin',
    unknownActor: 'Unbekannt',
    filters: {
      action: 'Aktion',
      actionPlaceholder: 'z. B. company.roles.*',
      outcome: 'Ergebnis',
      all: 'Alle',
      from: 'Von',
      to: 'Bis',
      reset: 'Filter zurücksetzen',
    },
    loadMore: 'Ältere laden',
    empty: 'Keine Einträge',
    emptyHint: 'Für diese Filter gibt es keine Einträge.',
    entry: 'Eintrag',
    occurredAt: 'Zeitpunkt',
    recordedAt: 'Erfasst',
    company: 'Firma',
    targetType: 'Objekttyp',
    targetId: 'Objekt-ID',
    requestId: 'Anfrage-ID',
    details: 'Details',
    selfChange: 'Am eigenen Konto',
    offline: 'Offline erfasst',
    actions: {
      account: {
        notifications: {
          'push-on': 'Push-Nachrichten auf einem Gerät eingeschaltet',
          'push-off': 'Push-Nachrichten auf einem Gerät ausgeschaltet',
        },
      },
      auth: {
        access: 'Zugriff',
        'company-switch': 'Firma gewechselt',
        login: 'Angemeldet',
        logout: 'Abgemeldet',
        passkey: { add: 'Passkey hinzugefügt', remove: 'Passkey entfernt' },
        password: { change: 'Passwort geändert', reset: 'Passwort über Link festgelegt' },
        'two-factor': {
          'backup-codes': 'Neue Backup-Codes erzeugt',
          disable: 'Authenticator-App entfernt',
          enable: 'Authenticator-App eingerichtet',
        },
      },
      core: {
        catalogs: {
          create: 'Katalogeintrag angelegt',
          delete: 'Katalogeintrag gelöscht',
          import: 'Katalog importiert',
          reorder: 'Katalog umsortiert',
          update: 'Katalogeintrag geändert',
        },
        findings: {
          add: 'Mangel erfasst',
          copy: 'Mängel übernommen',
          remove: 'Mangel gelöscht',
          update: 'Mangel geändert',
        },
        plans: { add: 'Plan hinzugefügt', remove: 'Plan entfernt', update: 'Plan geändert' },
        signatures: {
          read: 'Unterschrift für einen Bericht abgerufen',
          remove: 'Unterschrift entfernt',
          update: 'Unterschrift geändert',
        },
        templates: {
          create: 'Vorlage angelegt',
          delete: 'Vorlage gelöscht',
          update: 'Vorlage geändert',
        },
        'custom-fields': { read: 'Zusatzfelder abgerufen', update: 'Zusatzfelder geändert' },
        activity: { read: 'Verlauf abgerufen' },
        comments: {
          add: 'Kommentar geschrieben',
          remove: 'Kommentar gelöscht',
          update: 'Kommentar geändert',
        },
        documents: {
          add: 'Dokument in SharePoint abgelegt',
          folder: 'Ordner für Dokumente angelegt',
          link: 'SharePoint-Datei verknüpft',
          remove: 'Dokument in SharePoint entfernt',
          unlink: 'Verknüpfung zu SharePoint-Datei entfernt',
        },
        files: {
          add: 'Datei hinzugefügt',
          download: 'Datei abgerufen',
          'download-zip': 'Dateien als ZIP heruntergeladen',
          reject: 'Datei abgelehnt (Schadsoftware)',
          remove: 'Datei entfernt',
        },
        shares: {
          create: 'Für andere Firma freigegeben',
          'incoming-create': 'Freigabe einer anderen Firma erhalten',
          'incoming-revoke': 'Freigabe einer anderen Firma beendet',
          'incoming-update': 'Freigabe einer anderen Firma geändert',
          revoke: 'Freigabe beendet',
          update: 'Freigabe geändert',
        },
        sync: { reject: 'Offline-Änderung abgelehnt' },
        imports: { apply: 'Daten importiert' },
        reminders: {
          add: 'Wiedervorlage angelegt',
          update: 'Wiedervorlage geändert',
          remove: 'Wiedervorlage gelöscht',
        },
        sequences: { update: 'Nummernkreis geändert' },
        tasks: {
          add: 'Aufgabe angelegt',
          update: 'Aufgabe geändert',
          remove: 'Aufgabe gelöscht',
        },
        privacy: { retention: 'Löschfrist angewendet' },
        records: { merge: 'Dubletten zusammengeführt' },
        views: {
          create: 'Ansicht für die Firma gespeichert',
          delete: 'Ansicht der Firma gelöscht',
          update: 'Ansicht der Firma geändert',
        },
        workflows: { transition: 'Status geändert' },
      },
      company: {
        ai: { test: 'KI-Verbindung getestet', update: 'KI-Einstellungen geändert' },
        accounts: {
          create: 'Konto mit Passwort angelegt',
          invite: 'Einladung gesendet',
          link: 'Konten verknüpft',
          lock: 'Konto gesperrt',
          'password-reset': 'Link für neues Passwort gesendet',
          provision: 'Konto angelegt',
          'two-factor-reset': 'Authenticator-App zurückgesetzt',
          unlink: 'Verknüpfung gelöst',
          unlock: 'Konto entsperrt',
        },
        audit: { read: 'Audit-Log gelesen' },
        branding: {
          logo: 'Logo geändert',
          'logo-remove': 'Logo entfernt',
          update: 'Firmenfarbe geändert',
        },
        'custom-fields': {
          create: 'Zusatzfeld angelegt',
          delete: 'Zusatzfeld gelöscht',
          reorder: 'Zusatzfelder sortiert',
          update: 'Zusatzfeld geändert',
        },
        integrations: {
          test: 'SharePoint-Verbindung getestet',
          update: 'SharePoint-Anbindung geändert',
        },
        groups: {
          'add-member': 'Mitglied hinzugefügt',
          create: 'Gruppe angelegt',
          delete: 'Gruppe gelöscht',
          'remove-member': 'Mitglied entfernt',
          update: 'Gruppe geändert',
        },
        modules: { activation: 'Modul ein- oder ausgeschaltet', order: 'Module sortiert' },
        roles: {
          assign: 'Rolle zugewiesen',
          create: 'Rolle angelegt',
          delete: 'Rolle gelöscht',
          unassign: 'Zuweisung entfernt',
          update: 'Rolle geändert',
        },
        notifications: {
          test: 'Benachrichtigungskanal getestet',
          update: 'Benachrichtigungskanäle geändert',
        },
        privacy: {
          erase: 'Person gelöscht',
          export: 'Daten einer Person exportiert',
          'retention-update': 'Löschfrist geändert',
        },
        settings: { update: 'Einstellung geändert' },
        sso: {
          disable: 'SSO deaktiviert',
          'password-login': 'Anmeldung mit Passwort umgeschaltet',
          update: 'SSO geändert',
          verify: 'SSO getestet und aktiviert',
        },
      },
      platform: {
        audit: { read: 'Plattform-Audit gelesen' },
        backups: { create: 'Datenbank-Backup angefordert' },
        companies: { create: 'Firma angelegt', update: 'Firma geändert' },
        grants: { create: 'Plattformrecht vergeben', delete: 'Plattformrecht entzogen' },
        modules: { activation: 'Modul geschaltet' },
        settings: { update: 'Globale Einstellung geändert' },
      },
    },
  },
  ai: {
    title: 'KI',
    description:
      'KI-Funktionen der Module schicken Texte an ein Sprachmodell. Hier legt die Firma fest, über welchen Anbieter, mit welchem Schlüssel und mit welchen Daten.',
    enabled: 'KI-Funktionen eingeschaltet',
    enabledHint: 'Aus: Die Module zeigen keine KI-Knöpfe, der Kern lehnt jeden Aufruf ab.',
    provider: {
      title: 'Anbieter',
      description:
        'Anthropic (Claude) direkt oder OpenRouter, das an viele Modellanbieter weiterleitet. OpenRouter nimmt nur Anbieter ohne Training und ohne Speicherung der Anfragen.',
      label: 'Anbieter',
      platformKey: 'Die Plattform stellt einen Schlüssel, Standardmodell {{model}}.',
      noPlatformKey: 'Die Plattform hat keinen Schlüssel dafür. Ein eigener ist nötig.',
    },
    providers: { anthropic: 'Anthropic (Claude)', openrouter: 'OpenRouter' },
    model: 'Modell',
    modelHint: 'Für offene Aufgaben. Leer lassen für das Standardmodell der Plattform.',
    smallModel: 'Modell für einfache Aufgaben',
    smallModelHint:
      'Für eng geführte Funktionen wie den Mängelentwurf, günstiger und schneller. Leer lassen für das kleine Modell der Plattform.',
    smallModelTag: 'kleines Modell',
    filesTag: 'liest Dateien',
    effort: {
      title: 'Denkaufwand',
      description:
        'Wie gründlich das Modell nachdenkt, bevor es antwortet. Weniger Aufwand ist günstiger und schneller, mehr Aufwand gründlicher, aber teurer: Das Nachdenken kostet Tokens.',
      standard: 'Offene Aufgaben',
      small: 'Einfache Aufgaben',
      auto: 'Automatisch ({{level}})',
      autoHint: 'Automatisch nimmt die Vorgabe der Funktion, sonst den Wert in Klammern.',
      levels: { low: 'Niedrig', medium: 'Mittel', high: 'Hoch' },
    },
    modelInvalid: 'Kennung des Modells, z. B. claude-sonnet-5-5 oder anbieter/modell.',
    apiKey: 'Eigener API-Schlüssel',
    keyStored: 'Ein Schlüssel ist gespeichert. Leer lassen, um ihn zu behalten.',
    keyOptional: 'Optional. Ersetzt den Schlüssel der Plattform; die Kosten trägt dann die Firma.',
    keyMissing: 'Nötig, weil die Plattform keinen Schlüssel für diesen Anbieter hat.',
    keyRequired: 'Ohne Schlüssel lässt sich KI nicht einschalten.',
    keyInvalid: 'Der Schlüssel ist zu kurz.',
    removeKey: 'Eigenen Schlüssel entfernen',
    test: 'Verbindung testen',
    testSucceeded: '{{provider}} antwortet mit Modell {{model}}.',
    connectionTest: 'Verbindungstest',
    dataTitle: 'Daten',
    dataDescription:
      'Die höchste Stufe, die Funktionen an den Anbieter schicken dürfen. Die Firma entscheidet selbst; Stufen mit Personenbezug brauchen einen Vertrag zur Auftragsverarbeitung mit dem Anbieter. Besondere Kategorien wie Gesundheitsdaten gehen nie an ein externes Modell.',
    data: {
      none: {
        label: 'Ohne Personenbezug',
        short: 'Ohne Personenbezug',
        description: 'Nur Texte ohne Daten über Personen, etwa Formulierungshilfen.',
      },
      pseudonymized: {
        label: 'Pseudonymisiert',
        short: 'Pseudonymisiert',
        description:
          'Namen, E-Mail-Adressen, Telefonnummern und IBANs werden vor dem Versand durch Platzhalter ersetzt und in der Antwort zurückgesetzt.',
      },
      personal: {
        label: 'Personenbezogen',
        short: 'Personenbezogen',
        description: 'Daten über Personen gehen im Klartext an den Anbieter.',
      },
    },
    personalTitle: 'Nur mit Zustimmung des Datenschutzbeauftragten',
    personalWarning:
      'Personenbezogene Daten verlassen die App im Klartext. Der Zweck jeder Funktion muss im Verzeichnis der Verarbeitungstätigkeiten stehen.',
    limit: 'Monatsgrenze (Tokens)',
    limitHint:
      'Leer lassen für keine Grenze. Danach lehnt der Kern bis zum Monatsende ab. Eingaben aus dem Zwischenspeicher des Anbieters zählen zu einem Zehntel, das Schreiben dorthin zu 1,25.',
    limitInvalid: 'Eine ganze Zahl ab 1000.',
    monthCalls: 'Aufrufe in diesem Monat',
    monthTokens: 'Tokens in diesem Monat',
    functionsTitle: 'Funktionen',
    functionsHint:
      'KI-Funktionen der Module mit der Datenstufe, die sie brauchen. Eine Funktion über der gewählten Stufe bleibt gesperrt.',
    noFunctions: 'Kein Modul bietet bisher KI-Funktionen an.',
    columns: {
      function: 'Funktion',
      data: 'Daten',
      usage: 'Letzte 30 Tage',
      enabled: 'Eingeschaltet',
    },
    usageLine_one: '{{calls}} Aufruf, {{tokens}} Tokens',
    usageLine_other: '{{calls}} Aufrufe, {{tokens}} Tokens',
    functionEnabled: '{{name}} eingeschaltet',
    save: 'Speichern',
    saved: 'KI-Einstellungen gespeichert.',
    errors: {
      'ai-unavailable':
        'Der Anbieter ist nicht erreichbar oder der Schlüssel wird abgelehnt. Schlüssel und Modell prüfen.',
      'ai-failed': 'Der Anbieter hat keine verwertbare Antwort geliefert.',
      'ai-busy': 'Gerade zu viele Anfragen. Gleich noch einmal versuchen.',
      'ai-budget-exhausted': 'Die Monatsgrenze der Firma ist erreicht.',
      'key-required': 'Beim Wechsel des Anbieters bitte den Schlüssel neu eingeben oder entfernen.',
    },
  },
  companies: {
    title: 'Firmen',
    description:
      'Jede Firma ist ein eigener, getrennter Mandant. Teilgruppen ordnen sie nur, Daten sehen sie nicht.',
    newCompany: 'Neue Firma',
    newGroup: 'Neue Teilgruppe',
    kind: { company: 'Firma', group: 'Teilgruppe' },
    columns: { name: 'Name', kind: 'Art', slug: 'Kürzel', status: 'Status' },
    status: { active: 'Aktiv', disabled: 'Deaktiviert' },
    empty: 'Noch keine Firmen',
    name: 'Name',
    shortName: 'Kurzname',
    shortNameHint: 'Im Firmen-Umschalter, z. B. STE.',
    slug: 'Kürzel in Adressen',
    slugHint: 'Kleinbuchstaben, Ziffern und Bindestriche, mindestens zwei Zeichen.',
    parent: 'Übergeordnet',
    noParent: 'Oberste Ebene',
    color: 'Firmenfarbe',
    createCompany: 'Firma anlegen',
    createGroup: 'Teilgruppe anlegen',
    created: 'Angelegt',
    edit: 'Bearbeiten',
    editTitle: '{{name}} bearbeiten',
    saved: 'Gespeichert',
    disable: 'Deaktivieren',
    enable: 'Aktivieren',
    disableTitle: '{{name}} deaktivieren?',
    disableDescription:
      'Alle Sitzungen in dieser Firma enden sofort, und niemand kann sich mehr anmelden. Die Daten bleiben erhalten.',
    disabled: 'Deaktiviert',
    enabled: 'Aktiviert',
    invalidParent: 'Diese Einordnung geht nicht: Eine Firma kann nicht unter sich selbst stehen.',
    slugTaken: 'Dieses Kürzel ist schon vergeben.',
  },
  modules: {
    title: 'Module',
    description:
      'Der Schalter „Alle Firmen“ ist der Standard. Jede Firma kann Module unter Verwaltung › Module selbst ein- und ausschalten, ihr Schalter geht vor.',
    empty: 'Noch keine Module installiert',
    emptyHint: 'Module kommen als Pakete in die App und erscheinen dann hier.',
    global: 'Alle Firmen',
    company: 'Firma',
    inherit: 'Wie alle Firmen',
    on: 'An',
    off: 'Aus',
    groupScope: 'Gruppenweit',
    submoduleOf: 'Untermodul von {{parent}}',
    dependsOn: 'Braucht {{modules}}',
    saved: 'Gespeichert',
    perCompany: 'Pro Firma',
  },
  companyModules: {
    title: 'Module',
    description:
      'Welche Module die Firma nutzt und in welcher Reihenfolge sie in der Seitenleiste und auf der Startseite stehen. Ohne eigene Wahl gilt der Standard der Plattform. Ausgeschaltete Module verschwinden aus Navigation und Suche, ihre Daten bleiben erhalten.',
    standardOn: 'Standard: an',
    standardOff: 'Standard: aus',
    own: 'Eigene Wahl',
    reset: 'Standard übernehmen',
    needs: 'Wirkt erst, wenn {{modules}} an ist',
    saved: 'Gespeichert',
    moveUp: '„{{name}}“ nach oben',
    moveDown: '„{{name}}“ nach unten',
  },
  platformAccess: {
    title: 'Plattformrechte',
    description:
      'Rechte über allen Firmen. Nur wenige Personen, nur mit MFA, jede Änderung protokolliert.',
    grants: 'Vergebene Plattformrechte',
    grantsHint: 'Vergeben und entziehen kann nur ein anderer Superadmin.',
    grant: 'Recht vergeben',
    grantTitle: 'Plattformrecht vergeben',
    granted: 'Vergeben',
    revoke: '{{permission}} von {{name}} entziehen',
    revokeTitle: '{{permission}} entziehen?',
    revokeDescription: '{{name}} verliert das Recht sofort.',
    revokeConfirm: 'Entziehen',
    revoked: 'Entzogen',
    noGrants: 'Noch keine Plattformrechte vergeben.',
    company: 'Firma',
    chooseCompany: 'Firma wählen',
    person: 'Person',
    choosePerson: 'Person wählen',
    searchPerson: 'Name suchen',
    permission: 'Recht',
    grantedBy: 'Vergeben von',
    grantedAt: 'Vergeben am',
    cannotGrantSelf: 'Eigene Plattformrechte vergibt ein anderer Superadmin.',
    cannotRevokeSelf: 'Das eigene Superadmin-Recht entzieht ein anderer Superadmin.',
    links: 'Verknüpfte Konten',
    linksHint:
      'Verbindet die Konten einer Person in zwei Firmen. Der Firmenwechsel zeigt die andere Firma dann direkt an.',
    link: 'Konten verknüpfen',
    linkTitle: 'Konten verknüpfen',
    linked: 'Verknüpft',
    first: 'Erstes Konto',
    second: 'Zweites Konto',
    unlink: 'Verknüpfung lösen',
    unlinkTitle: 'Verknüpfung lösen?',
    unlinkDescription: 'Der Firmenwechsel braucht danach eine eigene Anmeldung.',
    unlinked: 'Verknüpfung gelöst',
    noLinks: 'Noch keine Konten verknüpft.',
    sameCompany: 'Bitte zwei verschiedene Firmen wählen.',
  },
  operations: {
    title: 'Betrieb',
    description:
      'Zustand der Plattform für alle Firmen. Nur Zahlen, keine Personendaten. Aktualisiert sich alle 30 Sekunden.',
    refresh: 'Aktualisieren',
    justNow: 'gerade eben',
    healthy: 'Keine Auffälligkeiten',
    problems: {
      title: 'Auffälligkeiten',
      migrations_one: '{{count}} Migration ist noch nicht angewandt.',
      migrations_other: '{{count}} Migrationen sind noch nicht angewandt.',
      jobs_one: '{{count}} Job ist in den letzten 24 Stunden endgültig fehlgeschlagen.',
      jobs_other: '{{count}} Jobs sind in den letzten 24 Stunden endgültig fehlgeschlagen.',
      events_one: '{{count}} Event-Zustellung ist in den letzten 24 Stunden fehlgeschlagen.',
      events_other: '{{count}} Event-Zustellungen sind in den letzten 24 Stunden fehlgeschlagen.',
      errors_one: '{{count}} unerwarteter Fehler seit dem Start des Servers.',
      errors_other: '{{count}} unerwartete Fehler seit dem Start des Servers.',
    },
    server: {
      title: 'Server',
      version: 'Version',
      commit: 'Commit',
      started: 'Gestartet',
      errors: 'Unerwartete Fehler seit Start',
    },
    database: {
      title: 'Datenbank',
      status: 'Status',
      reachable: 'Erreichbar, {{ms}} ms',
      connections: 'Verbindungen',
      pool: '{{total}} offen, {{idle}} frei, {{waiting}} wartend',
      migrations: 'Migrationen',
      applied: 'Alle {{count}} angewandt',
      pending: '{{count}} offen',
    },
    events: {
      title: 'Events',
      description: 'Zustellung an die Empfänger in Kern und Modulen.',
      due: 'Fällig',
      scheduled: 'Warten auf neuen Versuch',
      failed: 'Fehlgeschlagen (24 Stunden)',
      oldest: 'Älteste fällige',
    },
    notifications: {
      title: 'Benachrichtigungsversand',
      description: 'Fehler seit dem Start dieses Servers.',
      email: 'E-Mail',
      teams: 'Microsoft Teams',
      push: 'Push-Nachrichten',
    },
    ai: {
      title: 'KI heute',
      description: 'Alle Firmen zusammen, seit Mitternacht.',
      calls: 'Aufrufe',
      failures: 'Davon fehlgeschlagen',
      tokens: 'Tokens',
    },
    jobs: {
      title: 'Jobs',
      description:
        'Warteschlange nach Typ. Endgültig fehlgeschlagen heißt: alle Versuche verbraucht.',
      type: 'Typ',
      due: 'Fällig',
      running: 'Läuft',
      scheduled: 'Geplant',
      failed: 'Fehlgeschlagen (24 h)',
      empty: 'Keine Jobs eingerichtet',
    },
    backups: {
      title: 'Datenbank-Backups',
      description: 'Verschlüsselte Sicherungen der Datenbank, erstellt vom Server. Neueste zuerst.',
      create: 'Backup jetzt erstellen',
      confirmTitle: 'Backup jetzt erstellen?',
      confirmDescription:
        'Der Server sichert die Datenbank sofort. Das dauert meist einige Minuten, die STAW App bleibt nutzbar.',
      requestedToast: 'Backup angefordert',
      pending: 'Es läuft bereits ein Backup oder eines ist angefordert.',
      unavailable: 'Backups gibt es nur auf dem Server.',
      failed: 'Der Status der Backups lässt sich gerade nicht laden.',
      notAvailable: 'Keine Backups in dieser Umgebung',
      notAvailableHint:
        'Datenbank-Backups gibt es nur auf dem Server, nicht in der lokalen Entwicklung.',
      state: 'Status',
      running: 'Läuft',
      requested: 'Angefordert',
      last: 'Letztes Backup',
      lastSize: 'Größe',
      none: 'Noch keins',
      updatedAt: 'Stand',
      result: { success: 'Erfolgreich', failure: 'Fehlgeschlagen', running: 'Läuft' },
      list: 'Vorhandene Backups',
      createdAt: 'Erstellt',
      name: 'Datei',
      size: 'Größe',
      empty: 'Noch keine Backups vorhanden',
    },
  },
};
